import type { Observable} from 'rxjs';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Validation } from '@bayer-int/portletce-shared';

import type { Boot } from 'portlet/config/boot';
import { createUnknownError, createValidationError } from 'portlet/common/errors';
import type { HttpService } from 'portlet/common/services/HttpService';
import {
    resolveAdditionalAttributes,
    resolveBootPlaceholders,
} from 'portlet/common/utils/placeholders';
import type {
    PortletMetadata} from 'portlet/models/portletMetadata';
import {
    DocumentType,
    PortletSpecificationWithMetadata,
} from 'portlet/models/portletMetadata';
import { createSpecificationNotFoundError } from 'portlet/common/errors/SpecificationError';

import type { SpecificationId, SpecStatus } from '../models';
import { Specification } from '../models';

export class SpecificationService {
    private portletSpecUrl = this.boot.servers.portletSpec.url;

    constructor(
        private httpService: HttpService,
        private boot: Boot,
        private versionStatus?: SpecStatus,
    ) {}

    public getSpecification(
        configId: SpecificationId,
    ): Observable<{ metadata: PortletMetadata | null; spec: Specification }> {
        const url = `${this.portletSpecUrl}${configId}`;

        return this.getSpecificationFromUrl(url, DocumentType.PORTLET);
    }

    public getSpecificationFromUrl(
        configUrl: string,
        type?: DocumentType,
    ): Observable<{ metadata: PortletMetadata | null; spec: Specification }> {
        const query = {
            ...(this.versionStatus && { status: this.versionStatus }),
            ...(type && { type }),
            format: 'all',
        };

        return this.httpService.get(`${configUrl}`, { query, memoryCache: false }).pipe(
            map(({ data }) => data),
            map(
                Validation.create.toThrow(
                    PortletSpecificationWithMetadata.codec,
                    createValidationError,
                ),
            ),
            map(({ id, author, versionId, namespaceId, status, json }) => ({
                metadata: {
                    id,
                    author,
                    versionId,
                    namespaceId,
                    status,
                },
                spec: this.resolveBootPlaceholders(json),
            })),
            catchError(error => {
                throw error.status === 404
                    ? createSpecificationNotFoundError(error)
                    : createUnknownError(error);
            }),
        );
    }

    public validateAndResolveSpec(
        spec: unknown,
    ): Observable<{ metadata: PortletMetadata | null; spec: Specification }> {
        return of(spec).pipe(
            map(Validation.create.toThrow(Specification.codec, createValidationError)),
            map(spec => ({ metadata: null, spec: this.resolveBootPlaceholders(spec) })),
        );
    }

    public resolveAdditionalAttributes(
        spec: Specification,
        additionalAttrs: Record<string, string>,
    ): Specification {
        return resolveAdditionalAttributes(spec, additionalAttrs);
    }

    private resolveBootPlaceholders(spec: Specification): Specification {
        return resolveBootPlaceholders(spec, this.boot);
    }
}
